import * as React from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { ChevronsUpDown } from "lucide-react";
import { FormTooltip } from "@/components/ui/form-description";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface CountryDiallingCode {
  country: string;
  countryCode: string;
  code: string;
  flag: string;
}

interface TelephoneValue {
  diallingCode: string;
  localNumber: string;
  country?: string;
}

export interface TelephoneFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  placeholder?: string;
  diallingCodes?: readonly CountryDiallingCode[];
  dialCodesLabel?: string;
  dialCodesPlaceholder?: string;
  defaultCountry?: string;
}

const defaultDiallingCodes: readonly CountryDiallingCode[] = [
  { country: "Australia", countryCode: "AU", code: "+61", flag: "🇦🇺" },
  { country: "Austria", countryCode: "AT", code: "+43", flag: "🇦🇹" },
  { country: "Belgium", countryCode: "BE", code: "+32", flag: "🇧🇪" },
  { country: "Brazil", countryCode: "BR", code: "+55", flag: "🇧🇷" },
  { country: "Canada", countryCode: "CA", code: "+1", flag: "🇨🇦" },
  { country: "China", countryCode: "CN", code: "+86", flag: "🇨🇳" },
  { country: "Denmark", countryCode: "DK", code: "+45", flag: "🇩🇰" },
  { country: "Finland", countryCode: "FI", code: "+358", flag: "🇫🇮" },
  { country: "France", countryCode: "FR", code: "+33", flag: "🇫🇷" },
  { country: "Germany", countryCode: "DE", code: "+49", flag: "🇩🇪" },
  { country: "India", countryCode: "IN", code: "+91", flag: "🇮🇳" },
  { country: "Ireland", countryCode: "IE", code: "+353", flag: "🇮🇪" },
  { country: "Italy", countryCode: "IT", code: "+39", flag: "🇮🇹" },
  { country: "Japan", countryCode: "JP", code: "+81", flag: "🇯🇵" },
  { country: "Mexico", countryCode: "MX", code: "+52", flag: "🇲🇽" },
  { country: "Netherlands", countryCode: "NL", code: "+31", flag: "🇳🇱" },
  { country: "New Zealand", countryCode: "NZ", code: "+64", flag: "🇳🇿" },
  { country: "Norway", countryCode: "NO", code: "+47", flag: "🇳🇴" },
  { country: "Poland", countryCode: "PL", code: "+48", flag: "🇵🇱" },
  { country: "Singapore", countryCode: "SG", code: "+65", flag: "🇸🇬" },
  { country: "South Africa", countryCode: "ZA", code: "+27", flag: "🇿🇦" },
  { country: "South Korea", countryCode: "KR", code: "+82", flag: "🇰🇷" },
  { country: "Spain", countryCode: "ES", code: "+34", flag: "🇪🇸" },
  { country: "Sweden", countryCode: "SE", code: "+46", flag: "🇸🇪" },
  { country: "Switzerland", countryCode: "CH", code: "+41", flag: "🇨🇭" },
  { country: "United Kingdom", countryCode: "GB", code: "+44", flag: "🇬🇧" },
  { country: "United States", countryCode: "US", code: "+1", flag: "🇺🇸" },
] as const;

export function TelephoneField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  placeholder = "Enter phone number",
  diallingCodes = defaultDiallingCodes,
  dialCodesLabel = "Code",
  dialCodesPlaceholder = "Search country…",
  defaultCountry,
  className,
  ...htmlProps
}: Readonly<TelephoneFieldProps<TFieldValues, TName>>) {
  const [open, setOpen] = React.useState(false);

  const defaultCountryData = defaultCountry
    ? diallingCodes.find((dc) => dc.country === defaultCountry)
    : null;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // Initialize with default country if provided and field is empty
        if (defaultCountryData && !field.value?.diallingCode) {
          field.onChange({
            diallingCode: defaultCountryData.code,
            localNumber: "",
            country: defaultCountryData.country,
          });
        }
        // Always use structured value
        const value: TelephoneValue = (field.value as TelephoneValue) || {
          diallingCode: "",
          localNumber: "",
          country: "",
        };

        const handleDiallingCodeChange = (code: string, country: string) => {
          field.onChange({
            ...value,
            diallingCode: code,
            country: country,
          });
        };

        const handleLocalNumberChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          field.onChange({
            ...value,
            localNumber: e.target.value,
          });
        };

        // Find the selected country code
        // First try to match by both code and country name (most specific)
        // If country is not set or no match found, fall back to matching by code only
        const normalizedCountry = value.country?.trim();
        const selectedCode =
          (normalizedCountry
            ? diallingCodes.find(
                (dc) =>
                  dc.code === value.diallingCode &&
                  dc.country === normalizedCountry,
              )
            : undefined) ??
          diallingCodes.find((dc) => dc.code === value.diallingCode);

        return (
          <FormItem className={cn("flex flex-col", className)} {...htmlProps}>
            <div>
              <FormLabel className="inline-block leading-snug">
                {label}
                {required && (
                  <>
                    <span className="ms-1 text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">({requiredLabel})</span>
                  </>
                )}
              </FormLabel>
              {description && !descriptionAsTooltip && (
                <FormDescription>{description}</FormDescription>
              )}
              {description && descriptionAsTooltip && (
                <FormTooltip description={description} />
              )}
            </div>
            <div className="flex gap-2">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "min-w-32 justify-between bg-transparent hover:bg-transparent dark:bg-transparent",
                        !value.diallingCode && "text-muted-foreground",
                      )}
                    >
                      {selectedCode ? (
                        <span>
                          {selectedCode.flag} {selectedCode.code}
                        </span>
                      ) : (
                        dialCodesLabel
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder={dialCodesPlaceholder}
                      className="h-9 border-0 p-0 ring-0 outline-none"
                    />
                    <CommandList>
                      <CommandEmpty>No country found.</CommandEmpty>
                      <CommandGroup>
                        {diallingCodes.map((dc) => (
                          <CommandItem
                            value={`${dc.country} ${dc.code}`}
                            key={dc.country}
                            onSelect={() => {
                              handleDiallingCodeChange(dc.code, dc.country);
                              setOpen(false);
                            }}
                          >
                            <span className="mr-2">{dc.flag}</span>
                            <span className="flex-1">{dc.country}</span>
                            <span className="text-muted-foreground">
                              {dc.code}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormControl>
                <Input
                  type="tel"
                  placeholder={placeholder}
                  value={value.localNumber}
                  onChange={handleLocalNumberChange}
                  className="flex-1"
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
